import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { X, ChevronDown, Building } from "lucide-react";
import { api } from "../../lib/api";
import "./MultiSelectCompanies.css";

/**
 * Custom multi-select dropdown for companies
 * Clean, CRM-style UI with tag chips
 */
export default function MultiSelectCompanies({
  value = [],
  onChange,
  companies = null,
  required = false,
  disabled = false,
  placeholder = "Select companies...",
  className = "",
  hasError = false,
  testId = "multi-select-companies",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState(
    Array.isArray(companies) && companies.length > 0 ? companies : []
  );
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const fetchCompanies = useCallback(async () => {
    if (companies && Array.isArray(companies) && companies.length > 0) {
      setAvailableCompanies(companies);
      return;
    }

    setLoading(true);
    try {
      const res = await api("get", "/companies?active_only=true");
      if (res?.data && Array.isArray(res.data)) {
        const list = res.data
          .map((c) => (typeof c === "string" ? c : c.name || c.company_name))
          .filter(Boolean);
        setAvailableCompanies(list);
      }
    } catch (err) {
      console.error("Failed to load active companies:", err);
    } finally {
      setLoading(false);
    }
  }, [companies]);

  // Initial load
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const currentList = Array.isArray(value) ? value : [value].filter(Boolean);

  // Combine active companies from Settings with any already assigned historical/inactive companies in value
  const optionsToRender = useMemo(() => {
    const list = [...availableCompanies];
    currentList.forEach((c) => {
      if (c && !list.some((existing) => existing.toLowerCase() === c.toLowerCase())) {
        list.push(c);
      }
    });
    return list;
  }, [availableCompanies, currentList]);

  const handleToggleDropdown = () => {
    if (disabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen && (!companies || companies.length === 0)) {
      fetchCompanies();
    }
  };

  const handleToggleCompany = (company) => {
    if (disabled) return;

    const exists = currentList.some((c) => (c || "").toLowerCase() === company.toLowerCase());

    const newValue = exists
      ? currentList.filter((c) => (c || "").toLowerCase() !== company.toLowerCase())
      : [...currentList, company];

    onChange(newValue);
  };

  const handleRemoveCompany = (e, company) => {
    e.stopPropagation();
    if (disabled) return;

    const newValue = currentList.filter((c) => (c || "").toLowerCase() !== company.toLowerCase());
    onChange(newValue);
  };

  return (
    <div
      ref={containerRef}
      className={`multi-select-companies ${className}`}
      data-testid={testId}
    >
      <div
        className={`multi-select-comp-field ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${hasError ? "has-error" : ""}`}
        onClick={handleToggleDropdown}
        data-testid={`${testId}-trigger`}
      >
        <div className="multi-select-comp-content">
          {currentList.length === 0 ? (
            <span className="multi-select-comp-placeholder">{placeholder}</span>
          ) : (
            <div className="multi-select-comp-tags">
              {currentList.map((comp) => (
                <span key={comp} className="company-chip">
                  <Building size={11} />
                  {comp}
                  {!disabled && (
                    <button
                      type="button"
                      className="company-chip-remove"
                      onClick={(e) => handleRemoveCompany(e, comp)}
                      title={`Remove ${comp}`}
                      aria-label={`Remove ${comp}`}
                    >
                      <X size={10} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`multi-select-comp-arrow ${isOpen ? "open" : ""}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className="multi-select-comp-dropdown" data-testid={`${testId}-dropdown`}>
          <div className="multi-select-comp-header">
            <span>Select one or more companies</span>
            {required && <span className="req-hint">At least 1 required</span>}
          </div>
          <div className="multi-select-comp-options">
            {loading && optionsToRender.length === 0 ? (
              <div className="multi-select-comp-loading">Loading companies...</div>
            ) : optionsToRender.length === 0 ? (
              <div className="multi-select-comp-empty">No companies available</div>
            ) : (
              optionsToRender.map((comp) => {
                const isSelected = currentList.some((c) => (c || "").toLowerCase() === comp.toLowerCase());
                return (
                  <div
                    key={comp}
                    className={`multi-select-comp-option ${isSelected ? "selected" : ""}`}
                    onClick={() => handleToggleCompany(comp)}
                    data-testid={`${testId}-option-${comp}`}
                  >
                    <div className="option-comp-left">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="option-comp-checkbox"
                      />
                      <Building size={14} className="option-comp-icon" />
                      <span className="option-comp-name">{comp}</span>
                    </div>
                    {isSelected && (
                      <span className="option-comp-badge">Assigned</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
